import React from "react";
import Moment from "moment";
import commonUtilities from "utilities/common";
import { CONFIGURATION } from "utilities/configuration";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import ListItemText  from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { Icon } from "__ICON_REFERENCE__";
import Button from "@mui/material/Button";
import { withLocalizer } from "utilities/withLocalizer";

const filterOptions = [
    {
        value: "00:00:00-23:59:59",
        label: { en: "Anytime", fr: "​En tout temps" }
    },
    {
        value: "00:00:00-10:00:00",
        label: { en: "Morning(0:00-10:00)", fr: "Matin (entre minuit et 10 h)" }
    },
    {
        value: "09:00:00-13:00:00",
        label: { en: "Mid-morning(09:00-13:00)", fr: "Début de journée (entre 9 h et 13 h)" }
    },
    {
        value: "12:00:00-17:00:00",
        label: { en: "Afternoon(12:00-17:00)", fr: "Après-midi (entre midi et 17 h)" }
    },
    {
        value: "16:00:00-23:59:59",
        label: { en: "Evening(16:00-23:59)", fr: "Soirée (entre 16 h et 23 h 59)" }
    }
];

// Custom icon component
const ExpandMoreIcon = ({ className, ownerState, ...props }) => {
    // ownerState.open is true if the Select is open
    const iconName = ownerState?.open ? "chevron-up" : "chevron-down";

    return (
        <Icon
            {...props}
            className={className} // IMPORTANT: forwards MUI classes
            name={iconName}
            sx={{
                transition: "transform 0.2s" // optional if you want smooth rotation too
            }}
        />
    );
};

const Flight = (props) => {
    const { data, events } = props;
    const { flight, isInbound, index } = data;
    return <div className="flight-section">
        <div>
            <div className="flight-route">
                <div>
                    {flight?.departure?.name && <span>{flight?.departure?.name}</span>}
                    {flight?.departure?.code && <span className="flight-code">({flight?.departure?.code})</span>}
                    {flight?.departure?.name && flight?.arrival?.name && <Icon name="flight-solid" className="margin-right margin-left" />}
                </div>
                <div>
                    {flight?.arrival?.name && <span>{flight?.arrival?.name}</span>}
                    {flight?.arrival?.code && <span className="flight-code">({flight?.arrival?.code})</span>}
                </div>
            </div>
            <div>
                {flight?.departure?.date && <span>{Moment(flight?.departure?.date).format(CONFIGURATION.DATE_FORMAT_WITH_WEEK)}</span>}
                {flight?.duration && <><span className="dot-splitter"></span><span>{commonUtilities.formatDuration(flight?.duration)}</span></>}
            </div>
            <div className="flight-time">
                {flight?.departure?.time && <><span className="time-label departure">{props.localize("qma-depart")}:</span><span className="time-value">{flight?.departure?.time}</span></>}
                {flight?.arrival?.time && <><span className="time-label arrival">{props.localize("qma-arrive")}:</span><span className="time-value">{flight?.arrival?.time}</span></>}
            </div>
        </div>
        <div className="flight-selector-col">
            <div>
                <img className="airline-logo" src={CONFIGURATION.WEST_JET_AIRLINE_LOGO} alt="Westjet logo"/>
            </div>
            {flight?.flightNo&&<div className="flight-no">{flight?.flightNo}</div>}
            <div className="cmd-flight-selection">
                {flight.selected ? 
                    <Button variant="contained" className="btn-contained">{props.localize("qma-btn-selected-label")}</Button>
                    :
                    <Button variant="outlined" className="btn-outlined" onClick={() => events?.handleFightSelectionChange(index, isInbound)}>{props.localize("qma-btn-select-this-flight-label")}</Button>
                }
            </div>
        </div>
    </div>;
};

const RouteHeader = (props) => {
    const { data, events } = props;
    const { departure, destination, departureDate, flightFilter, isInbound, languageCode } = data;

    const getOptionLabel = (label, languageCode) => {
        if(!label) { return ""; }
        if(languageCode.toLowerCase() === "fr") {
            return label.fr;
        }
        else {
            return label.en;
        }
    };

    return <div className="flight-route-wrapper">
        <div>
            <div className="flight-route">
                <div>
                    {departure?.name && <span>{departure?.name}</span>}
                    {departure?.providerCode && <span className="flight-code">({departure?.providerCode})</span>}
                    {departure?.name && destination?.name && <Icon name="flight-solid" className="margin-right margin-left" />}
                </div>
                <div>
                    {destination?.name && <span>{destination?.name}</span>}
                    {destination?.providerCode && <span className="flight-code">({destination?.providerCode})</span>}
                </div>
            </div>
            <div className="flight-date">
                {departureDate && <span>{Moment(departureDate).format(CONFIGURATION.DATE_FORMAT_WITH_WEEK)}</span>}
            </div>
        </div>
        <div>
            <FormControl variant="filled" sx={{ m: 1, minWidth: 350 }} className="form-control select">
                <InputLabel id="outbound-filter-select-filled-label">{props.localize("qma-flight-filter-label")}</InputLabel>
                <Select
                    labelId="outbound-filter-select-filled-label"
                    id="outbound-filter-select-filled"
                    value={flightFilter}
                    IconComponent={ExpandMoreIcon}
                    MenuProps={{
                        PaperProps: {
                            className: "menu-dropdown-list"
                        }
                    }}
                    renderValue={(selected) => getOptionLabel(filterOptions.find(o => o.value === selected)?.label, languageCode) ?? ""}
                    onChange={(event) => events?.handleFlightFilterChange && events.handleFlightFilterChange(event.target.value, isInbound)}
                >
                    {
                        filterOptions.map(option => 
                            <MenuItem key={option.value} value={option.value} selected={option.value === flightFilter} className="my-menu-item">
                                <ListItemText primary={getOptionLabel(option.label, languageCode)} />
                                <ListItemIcon
                                    sx={{
                                        minWidth: 32,
                                        visibility: option.value === flightFilter ? "visible" : "hidden"
                                    }}
                                >
                                    <Icon name="checkmark"/>
                                </ListItemIcon>
                            </MenuItem>
                        )
                    }
                </Select>
            </FormControl>
        </div>
    </div>;
};

function CreateQuoteStepThree(props) {
    const { data, events, errors } = props;
    const { quote, currentStep, optionIndex } = data;

    if (currentStep !== CONFIGURATION.QUOTE_WIZARD_STEP_FLIGHT_COLLECTION) {
        return null;
    }

    const selectedProductType = (quote?.productTypes || []).at(optionIndex);
    if(!selectedProductType) return null;

    const flights = selectedProductType?.flights;
    if(flights?.data?.status !== "calculated") return null;

    const inboundFlights = flights?.data?.inboundFlights || [];
    const outboundFlights = flights?.data?.outboundFlights || [];

    const outboundFlightFilter = selectedProductType.flights?.data?.outboundFlightFilter || "";
    const inboundFlightFilter = selectedProductType.flights?.data?.inboundFlightFilter || "";

    const languageCode = commonUtilities.getLanguage() || "en";

    return (
        <div className="row wizard-step">
            <div className="row">
                <div className="option-heading margin-bottom">
                    { props.localize("qma-select-your-flight") }
                </div>
                <div className="margin-bottom-1-4">
                    {selectedProductType?.departure?.name && <span>{selectedProductType?.departure?.name}</span>}
                    {selectedProductType?.departure?.providerCode && <span> ({selectedProductType?.departure?.providerCode})</span>}
                    {selectedProductType?.departure?.name && selectedProductType?.destination?.name && <span> - </span>}
                    {selectedProductType?.destination?.name && <span>{selectedProductType?.destination?.name}</span>}
                    {selectedProductType?.destination?.providerCode && <span> ({selectedProductType?.destination?.providerCode})</span>}
                </div>
                <div>
                    {selectedProductType?.departureDate && <span>{Moment(selectedProductType.departureDate).format(CONFIGURATION.DATE_FORMAT_WITH_WEEK)}</span>}
                    {selectedProductType?.departureDate && selectedProductType?.returnDate && <span> - </span>}
                    {selectedProductType?.returnDate && <span>{Moment(selectedProductType.returnDate).format(CONFIGURATION.DATE_FORMAT_WITH_WEEK)}</span>}
                    {selectedProductType?.noOfAdults && <span> • {selectedProductType?.noOfAdults} {props.localize("qma-adults")} </span>}
                    {<span> , {Number(selectedProductType?.noOfChildren ?? 0)} {props.localize((Number(selectedProductType?.noOfChildren ?? 0) > 1) ? "qma-children" : "qma-child")}</span>}
                </div>
            </div>
            <div className="row wizard-section margin-top-2x">
                <div className="flight-direction-heading">
                    { props.localize("qma-departing") }
                </div>
                <RouteHeader 
                    data={{ 
                        departure: selectedProductType?.departure, 
                        destination: selectedProductType?.destination, 
                        departureDate: selectedProductType?.departureDate, 
                        flightFilter: outboundFlightFilter, 
                        isInbound: false,
                        languageCode
                    }} 
                    events={{ 
                        handleFlightFilterChange: (value, isInbound) => events?.handleFlightFilterChange && events.handleFlightFilterChange(value, isInbound)
                    }}
                    localize={props.localize}
                />
                { outboundFlights.map((flight, index) => commonUtilities.flightIncluded(outboundFlightFilter, flight?.departure?.time) ?
                    <Flight
                        key={"flight-" + index} 
                        data={{ 
                            flight: flight, 
                            isInbound: false, 
                            index: index 
                        }} 
                        events={{ 
                            handleFightSelectionChange: () => events?.handleFightSelectionChange && events.handleFightSelectionChange(index, false) 
                        }}
                        localize={props.localize}
                    />
                    : null
                )}
            </div>
            <div className="row wizard-section margin-top-1x">
                <div className="flight-direction-heading">
                    { props.localize("qma-returning") }
                </div>
                <RouteHeader 
                    data={{ 
                        departure: selectedProductType?.destination, 
                        destination: selectedProductType?.departure, 
                        departureDate: selectedProductType?.returnDate, 
                        flightFilter: inboundFlightFilter, 
                        isInbound: true,
                        languageCode
                    }} 
                    events={{ 
                        handleFlightFilterChange: (value, isInbound) => events?.handleFlightFilterChange && events.handleFlightFilterChange(value, isInbound) 
                    }} 
                    localize={props.localize}
                />
                { inboundFlights.map((flight, index) => commonUtilities.flightIncluded(inboundFlightFilter, flight?.departure?.time) ?
                    <Flight 
                        key={"flight-" + index} 
                        data={{ 
                            flight: flight, 
                            isInbound: true, 
                            index: index 
                        }} 
                        events={{ 
                            handleFightSelectionChange:  () => events?.handleFightSelectionChange && events.handleFightSelectionChange(index, true)  
                        }}
                        localize={props.localize}
                    />
                    : null)}
            </div>
            <div className="margin-top-2x">
                { props.localize("qma-flight-selection-notice") }
            </div>
        </div>
    );
}


export default withLocalizer(CreateQuoteStepThree);